import React, { useState, useRef } from "react";
import { Upload, X, Check, Info, AlertCircle, Image as ImageIcon } from "lucide-react";
import { toast } from "react-toastify";

/**
 * Image Upload Component with Preview
 * Supports drag & drop, click to browse, and base64 conversion
 */
const ImageUpload = ({
  value,
  onChange,
  maxSize = 5,
  acceptedFormats = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
  previewUrl = null,
  showPreview = true,
  className = "",
}) => {
  const fileInputRef = useRef(null);
  const [imagePreview, setImagePreview] = useState(previewUrl);
  const [imageSource, setImageSource] = useState(previewUrl ? "url" : null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);

  // File to Base64 converter
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Validate file
  const validateFile = (file) => {
    setError(null);

    if (!file.type.startsWith("image/")) {
      const errorMsg = "Please upload an image file";
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    if (!acceptedFormats.includes(file.type)) {
      const errorMsg = `Accepted formats: ${acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')}`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    if (file.size > maxSize * 1024 * 1024) {
      const errorMsg = `Image size must be less than ${maxSize}MB`;
      setError(errorMsg);
      toast.error(errorMsg);
      return false;
    }

    return true;
  };

  // Handle file selection
  const handleFileSelect = async (file) => {
    if (!file) return;

    if (!validateFile(file)) return;

    try {
      const base64 = await convertToBase64(file);
      setImagePreview(base64);
      setImageSource("base64");
      onChange(base64);
      toast.success("Image uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload image");
      setError("Failed to process image");
    }
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  // Remove image
  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageSource(null);
    setError(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={className}>
      {/* Image Preview */}
      {showPreview && imagePreview ? (
        <div className="mb-6">
          <div className="inline-block relative group">
            <div className="overflow-hidden w-48 h-48 bg-white rounded-xl border-2 border-gray-200 shadow-lg">
              <img
                src={imagePreview}
                alt="Logo preview"
                className="object-cover w-full h-full transition-transform group-hover:scale-105"
              />
              {/* Overlay on hover */}
              <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-0 rounded-xl transition-all duration-200 group-hover:bg-opacity-30">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  className="flex items-center px-4 py-2 space-x-2 text-sm font-medium text-gray-900 bg-white rounded-lg shadow-lg opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-50"
                >
                  <ImageIcon className="w-4 h-4" />
                  <span>Change</span>
                </button>
              </div>
            </div>
            {/* Remove button */}
            <button
              type="button"
              onClick={handleRemoveImage}
              className="absolute -top-2 -right-2 z-10 p-2 text-white bg-red-500 rounded-full shadow-lg transition-colors hover:bg-red-600"
              title="Remove logo"
            >
              <X className="w-4 h-4" />
            </button>
            {/* Status badge */}
            {imageSource === "url" && (
              <div className="absolute top-2 left-2">
                <span className="inline-flex items-center px-2 py-1 text-xs text-white bg-blue-600 rounded-md shadow">
                  <Info className="mr-1 w-3 h-3" />
                  Current
                </span>
              </div>
            )}
            {imageSource === "base64" && (
              <div className="absolute top-2 left-2">
                <span className="inline-flex items-center px-2 py-1 text-xs text-white bg-green-600 rounded-md shadow">
                  <Check className="mr-1 w-3 h-3" />
                  New
                </span>
              </div>
            )}
          </div>
          {/* Change Logo Button - Always visible */}
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="mt-4 flex items-center justify-center space-x-2 px-4 py-2.5 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg border border-blue-200 transition-all font-medium text-sm shadow-sm hover:shadow"
          >
            <Upload className="w-4 h-4" />
            <span>Change Logo</span>
          </button>
        </div>
      ) : (
        /* Upload Area */
        <div>
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
              isDragging
                ? "bg-blue-50 border-blue-500"
                : error
                ? "bg-red-50 border-red-300"
                : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"
            }`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="flex flex-col items-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
                error ? "bg-red-100" : "bg-gray-100"
              }`}>
                {error ? (
                  <AlertCircle className="w-8 h-8 text-red-500" />
                ) : (
                  <Upload className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <p className={`text-base font-semibold mb-1 ${
                error ? "text-red-900" : "text-gray-900"
              }`}>
                {isDragging ? "Drop your image here" : error ? "Upload Failed" : "Upload Logo"}
              </p>
              <p className="mb-4 text-sm text-gray-600">
                {error || "Drag and drop or click to browse"}
              </p>
              <p className="text-xs text-gray-500">
                {acceptedFormats.map(f => f.split('/')[1].toUpperCase()).join(', ')} up to {maxSize}MB
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFormats.join(",")}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default ImageUpload;

